import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import api from '../../config/api';
import { UserCircle, Lock, ArrowRight, Shield } from 'lucide-react';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check if already logged in as admin
  useEffect(() => {
    const userType = localStorage.getItem('userType');
    const token = localStorage.getItem('token');
    
    if (token && userType === 'admin') {
      navigate('/admin/dashboard');
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Use the admin login endpoint with the correct URL
      const response = await api.post('/admin/login', formData);
      
      // Extract token and user data
      const { token, user } = response.data;
      
      // Save admin data in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('userType', 'admin');
      
      // Set expiration date for 30 days
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + 30);
      localStorage.setItem('tokenExpiration', expirationDate.toISOString());
      
      // Set token in axios headers
      api.defaults.headers.common['x-auth-token'] = token;
      
      toast.success('Admin login successful!');
      
      // Use setTimeout to ensure the navigation happens after the state updates
      setTimeout(() => {
        navigate('/admin/dashboard');
      }, 100);
    } catch (err) {
      console.error('Admin login error:', err);
      toast.error(err.response?.data?.message || 'Invalid admin credentials');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-white flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="max-w-5xl w-full mx-auto overflow-hidden rounded-2xl shadow-2xl"
      >
        <div className="grid md:grid-cols-5">
          {/* Left side - Image with Overlay */}
          <div className="relative hidden md:block md:col-span-2 bg-gradient-to-br from-red-600 to-red-900">
            <div className="absolute inset-0 bg-pattern opacity-10"></div>
            <div className="absolute inset-0 flex flex-col justify-between p-10">
              <div className="flex items-center space-x-2">
                <Shield className="h-8 w-8 text-white" />
                <span className="text-white text-xl font-bold">BloodHero</span>
              </div>
              
              <div className="space-y-6">
                <h3 className="text-3xl font-bold text-white">Admin Portal</h3>
                <p className="text-white/90 text-base max-w-xs">
                  Manage your blood donation system efficiently and securely.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <UserCircle className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Donor Management</p>
                      <p className="text-white/70 text-sm">Track and manage all donors</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2L8 6H4v4l-4 4 4 4v4h4l4 4 4-4h4v-4l4-4-4-4V6h-4L12 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-white font-medium">Blood Inventory</p>
                      <p className="text-white/70 text-sm">Monitor blood supply levels</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="text-white/50 text-sm">
                2023 BloodHero. All rights reserved.
              </div>
            </div>
          </div>

          {/* Right side - Form */}
          <div className="p-8 md:p-12 md:col-span-3 bg-white">
            <div className="max-w-md mx-auto">
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-gray-900">Welcome Back</h2>
                <p className="text-gray-600 mt-2">Sign in to your admin account to continue</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <UserCircle className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="admin@example.com"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="password"
                      name="password"
                      required
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      id="remember-me"
                      name="remember-me"
                      type="checkbox"
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    />
                    <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                      Remember me
                    </label>
                  </div>
                  <div className="text-sm">
                    <a href="#" className="font-medium text-red-600 hover:text-red-500">
                      Forgot password?
                    </a>
                  </div>
                </div>

                <motion.button
                  type="submit"
                  className="w-full flex items-center justify-center bg-gradient-to-r from-red-600 to-red-700 text-white py-3 px-4 rounded-lg font-medium hover:from-red-700 hover:to-red-800 transition-all shadow-md"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    'Signing in...'
                  ) : (
                    <>
                      Sign in to Dashboard
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </motion.button>

                <div className="text-center mt-8">
                  <Link
                    to="/"
                    className="text-sm text-gray-600 hover:text-red-600 inline-flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                    Return to homepage
                  </Link>
                </div>
              </form>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;